<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import MetaConnectionAPI from 'dashboard/api/metaConnection';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');

const loading = ref(true);
const loadError = ref(false);
const saving = ref(false);
const unlinking = ref(false);
const pageId = ref('');
const status = ref({});
const unlinkDialogRef = ref(null);

const fb = computed(() => status.value.facebook || { connected: false });
const ig = computed(() => status.value.instagram || { connected: false });
const anyConnected = computed(() => fb.value.connected || ig.value.connected);
const fbStatusText = computed(() =>
  fb.value.connected
    ? fb.value.page_name || fb.value.page_id
    : t('INTEGRATION_SETTINGS.META.NOT_LINKED')
);
const igStatusText = computed(() =>
  ig.value.connected
    ? `@${ig.value.username}`
    : t('INTEGRATION_SETTINGS.META.NOT_LINKED')
);

// Nomes de variável de ambiente, webhooks e verify token são da instalação,
// não do cliente: só o super admin configura o app Meta.
const isSuperAdmin = computed(
  () => currentUser.value?.type === 'SuperAdmin'
);

const loadStatus = async () => {
  try {
    const { data } = await MetaConnectionAPI.getStatus();
    status.value = data || {};
    loadError.value = false;
  } catch (e) {
    // Falha ao carregar não é "não vinculado": mostra erro com nova tentativa.
    loadError.value = true;
  }
};

const reload = async () => {
  loading.value = true;
  await loadStatus();
  loading.value = false;
};

onMounted(reload);

const connect = async () => {
  if (!pageId.value.trim()) {
    useAlert(t('INTEGRATION_SETTINGS.META.PAGE_ID_REQUIRED'));
    return;
  }
  saving.value = true;
  try {
    const { data } = await MetaConnectionAPI.connect(pageId.value.trim());
    status.value = data;
    pageId.value = '';
    const now = data.connected_now || {};
    const parts = [];
    if (now.facebook?.connected) {
      parts.push(
        t('INTEGRATION_SETTINGS.META.LINKED_FACEBOOK', {
          name:
            now.facebook.page_name ||
            t('INTEGRATION_SETTINGS.META.PAGE_FALLBACK'),
        })
      );
    }
    if (now.instagram?.connected) {
      parts.push(
        t('INTEGRATION_SETTINGS.META.LINKED_INSTAGRAM', {
          username: now.instagram.username || '',
        })
      );
    }
    useAlert(
      parts.length
        ? t('INTEGRATION_SETTINGS.META.LINKED', {
            channels: parts.join(` ${t('INTEGRATION_SETTINGS.META.AND')} `),
          })
        : t('INTEGRATION_SETTINGS.META.LINK_UPDATED')
    );
    // avisos do que NÃO conectou (ex.: página sem Instagram vinculado)
    if (now.instagram && !now.instagram.connected && now.instagram.error) {
      useAlert(now.instagram.error);
    }
  } catch (e) {
    useAlert(
      e?.response?.data?.error || t('INTEGRATION_SETTINGS.META.LINK_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const openUnlinkDialog = () => unlinkDialogRef.value?.open();

const disconnect = async () => {
  unlinking.value = true;
  try {
    await MetaConnectionAPI.disconnect();
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.META.UNLINKED'));
    unlinkDialogRef.value?.close();
  } catch (e) {
    useAlert(t('INTEGRATION_SETTINGS.META.UNLINK_ERROR'));
  } finally {
    unlinking.value = false;
  }
};

const copy = (text, msgKey) => {
  copyTextToClipboard(text);
  useAlert(t(msgKey));
};

const pageWebhookUrl = computed(
  () => status.value.page_webhook_url || status.value.leadgen_webhook_url
);

const goIg = () => router.push({ name: 'instagram_automation_index' });
const goFb = () => router.push({ name: 'facebook_automation_index' });
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto">
    <div class="w-full max-w-[60rem] mx-auto p-6 flex flex-col gap-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.META.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('INTEGRATION_SETTINGS.META.DESCRIPTION') }}
        </p>
      </header>

      <div v-if="loading" class="flex justify-center py-10 text-n-slate-11">
        <Spinner />
      </div>

      <section
        v-else-if="loadError"
        role="alert"
        class="flex flex-col items-start gap-3 p-4 rounded-2xl border border-n-ruby-6 bg-n-ruby-2"
      >
        <p class="flex items-center gap-2 mb-0 text-sm text-n-ruby-11">
          <span
            class="i-ph-warning-circle size-4 shrink-0"
            aria-hidden="true"
          />
          {{ t('INTEGRATION_SETTINGS.META.LOAD_ERROR') }}
        </p>
        <Button
          :label="t('INTEGRATION_SETTINGS.META.RETRY')"
          icon="i-ph-arrow-clockwise"
          variant="faded"
          color="slate"
          size="sm"
          @click="reload"
        />
      </section>

      <template v-else>
        <section
          class="flex flex-col gap-4 p-4 rounded-2xl border border-n-weak bg-n-alpha-1"
        >
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.META.CONNECTION') }}
          </h2>

          <!-- Status por canal -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2 text-sm">
              <span
                :class="[
                  fb.connected
                    ? 'i-ri-checkbox-circle-line text-n-teal-11'
                    : 'i-ri-checkbox-blank-circle-line text-n-slate-10',
                  'size-4',
                ]"
                aria-hidden="true"
              />
              <span
                :class="fb.connected ? 'text-n-slate-12' : 'text-n-slate-10'"
              >
                <strong>{{ t('INTEGRATION_SETTINGS.META.FACEBOOK') }}</strong>:
                {{ fbStatusText }}
              </span>
            </div>
            <div class="flex items-center gap-2 text-sm">
              <span
                :class="[
                  ig.connected
                    ? 'i-ri-checkbox-circle-line text-n-teal-11'
                    : 'i-ri-checkbox-blank-circle-line text-n-slate-10',
                  'size-4',
                ]"
                aria-hidden="true"
              />
              <span
                :class="ig.connected ? 'text-n-slate-12' : 'text-n-slate-10'"
              >
                <strong>{{ t('INTEGRATION_SETTINGS.META.INSTAGRAM') }}</strong>:
                {{ igStatusText }}
              </span>
            </div>
          </div>

          <!-- Form de vínculo -->
          <div class="flex flex-col gap-2 pt-1">
            <Input
              v-model="pageId"
              :label="
                anyConnected
                  ? t('INTEGRATION_SETTINGS.META.PAGE_ID_LABEL_REPLACE')
                  : t('INTEGRATION_SETTINGS.META.PAGE_ID_LABEL')
              "
              :placeholder="t('INTEGRATION_SETTINGS.META.PAGE_ID_PLACEHOLDER')"
            />
            <p class="text-xs text-n-slate-10">
              {{ t('INTEGRATION_SETTINGS.META.PAGE_ID_HELP') }}
            </p>
            <div class="flex gap-2">
              <Button
                :label="
                  anyConnected
                    ? t('INTEGRATION_SETTINGS.META.RELINK')
                    : t('INTEGRATION_SETTINGS.META.LINK')
                "
                size="sm"
                :is-loading="saving"
                @click="connect"
              />
              <Button
                v-if="anyConnected"
                :label="t('INTEGRATION_SETTINGS.META.UNLINK')"
                variant="faded"
                color="ruby"
                size="sm"
                icon="i-ri-plug-line"
                @click="openUnlinkDialog"
              />
            </div>
          </div>

          <!-- Configuração do app Meta (instância): os webhooks do MESMO app e
               secret/verify/token compartilhados. Só para super admin, que é
               quem configura o app (Formulários Meta, comentários FB e IG). -->
          <div
            v-if="isSuperAdmin"
            class="flex flex-col gap-2 p-3 rounded-xl bg-n-alpha-2 border border-n-weak text-xs text-n-slate-11"
          >
            <span class="flex items-start gap-1.5 text-n-slate-12">
              <span
                class="i-ri-information-line size-4 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <span>
                <strong>
                  {{ t('INTEGRATION_SETTINGS.META.APP_SETUP_TITLE') }}
                </strong>
                {{ t('INTEGRATION_SETTINGS.META.APP_SETUP_DESCRIPTION') }}
              </span>
            </span>
            <div class="flex flex-col gap-1">
              <span>
                {{ t('INTEGRATION_SETTINGS.META.PAGE_WEBHOOK') }}
                <code class="break-all">{{ pageWebhookUrl }}</code>
                <Button
                  :label="t('INTEGRATION_SETTINGS.META.COPY')"
                  variant="link"
                  size="xs"
                  @click="
                    copy(
                      pageWebhookUrl,
                      'INTEGRATION_SETTINGS.META.COPIED_PAGE_WEBHOOK'
                    )
                  "
                />
              </span>
              <span>
                {{ t('INTEGRATION_SETTINGS.META.IG_WEBHOOK') }}
                <code class="break-all">{{ status.ig_webhook_url }}</code>
                <Button
                  :label="t('INTEGRATION_SETTINGS.META.COPY')"
                  variant="link"
                  size="xs"
                  @click="
                    copy(
                      status.ig_webhook_url,
                      'INTEGRATION_SETTINGS.META.COPIED_IG_WEBHOOK'
                    )
                  "
                />
              </span>
              <span v-if="status.verify_token">
                {{ t('INTEGRATION_SETTINGS.META.VERIFY_TOKEN') }}
                <code class="break-all">{{ status.verify_token }}</code>
                <Button
                  :label="t('INTEGRATION_SETTINGS.META.COPY')"
                  variant="link"
                  size="xs"
                  @click="
                    copy(
                      status.verify_token,
                      'INTEGRATION_SETTINGS.META.COPIED_VERIFY_TOKEN'
                    )
                  "
                />
              </span>
            </div>
          </div>
        </section>

        <!-- Atalhos pras automações -->
        <section v-if="anyConnected" class="flex flex-col gap-2">
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.META.AUTOMATIONS') }}
          </h2>
          <p class="text-xs text-n-slate-11">
            {{ t('INTEGRATION_SETTINGS.META.AUTOMATIONS_HINT') }}
          </p>
          <div class="flex gap-2">
            <Button
              v-if="ig.connected"
              :label="t('INTEGRATION_SETTINGS.META.IG_AUTOMATION')"
              variant="faded"
              color="slate"
              size="sm"
              icon="i-ri-instagram-line"
              @click="goIg"
            />
            <Button
              v-if="fb.connected"
              :label="t('INTEGRATION_SETTINGS.META.FB_AUTOMATION')"
              variant="faded"
              color="slate"
              size="sm"
              icon="i-ri-facebook-line"
              @click="goFb"
            />
          </div>
        </section>
      </template>
    </div>

    <Dialog
      ref="unlinkDialogRef"
      type="alert"
      :title="t('INTEGRATION_SETTINGS.META.UNLINK_CONFIRM_TITLE')"
      :description="t('INTEGRATION_SETTINGS.META.UNLINK_CONFIRM_DESCRIPTION')"
      :confirm-button-label="t('INTEGRATION_SETTINGS.META.UNLINK')"
      :cancel-button-label="t('INTEGRATION_SETTINGS.META.CANCEL')"
      :is-loading="unlinking"
      @confirm="disconnect"
    />
  </div>
</template>
